import type { ReactElement } from "react";
import { useCallback, useEffect } from "react";

import {
  KeyboardArrowLeftIcon,
  KeyboardArrowRightIcon,
} from "@braintrust/braintrust-ui-components/Icons";

import styles from "./style.module.scss";

/**
 * Usage — ModalSlider is a controlled component, the parent owns the index:
 *
 * ```
 * const [index, setIndex] = useState(0);
 *
 * <ModalSlider
 *     index={index}
 *     setIndex={setIndex}
 *     items={[
 *         <Item key="1" title="Foo" />,
 *         <Item key="2" title="Bar" />,
 *         <Item key="3" title="Baz" />,
 *     ]} />
 * ```
 */

type ModalSliderProps = {
  items: ReactElement[];
  index: number;
  setIndex: (index: number) => void;
};

export const ModalSlider = ({
  items = [],
  index,
  setIndex,
}: ModalSliderProps) => {
  const onChangeSlide = useCallback(
    (direction: 1 | -1) => {
      const newIndex = index + direction;

      if (newIndex >= 0 && newIndex < items.length) {
        setIndex(newIndex);
      }
    },
    [index, items.length, setIndex],
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
        onChangeSlide(e.key === "ArrowRight" ? 1 : -1);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onChangeSlide]);

  if (!items) {
    return null;
  }

  return (
    <div className="text-content">
      {items[index]}
      {!!items.length && index > 0 && (
        <KeyboardArrowLeftIcon
          className={styles.modalSliderContentPrev}
          onClick={() => onChangeSlide(-1)}
        />
      )}
      {!!items.length && index < items.length - 1 && (
        <KeyboardArrowRightIcon
          className={styles.modalSliderContentNext}
          onClick={() => onChangeSlide(1)}
        />
      )}
    </div>
  );
};
